import { Fragment } from "react";

import { History, Pencil, Trash2 } from "lucide-react";

import { ButtonV4, ChipV4 } from "../common/ui";
import formatComma from "react-lib/utils/formatComma";

import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  codeOf,
  createdTimeOf,
  isLogKind,
  priceOf,
  type DoneRow,
} from "./ordersModelV4";
import type { DoneGroup } from "./orderListModelV4";

// Types
type OrderDoneTableV4Props = {
  // data
  groups: DoneGroup[];
  // callback
  onEdit: (row: DoneRow) => void;
  onCancel: (row: DoneRow) => void;
  onLog: (row: DoneRow) => void;
};

/** คำสั่งที่ยืนยันแล้ว แยกกลุ่มตามประเภท */
const OrderDoneTableV4 = (props: OrderDoneTableV4Props) => {
  // Render
  const renderActions = (row: DoneRow) => {
    if (row.isCancelled) {
      return null;
    }

    if (row.isLocked) {
      return (
        <span title="ปิดแล้ว แก้ไขไม่ได้" aria-label="ปิดแล้ว" className="hv4-ord-lock">
          🔒
        </span>
      );
    }

    return (
      <>
        {row.canEdit && !row.isEditing && (
          <ButtonV4
            variant="ghost"
            size="icon"
            aria-label={`แก้ไข ${codeOf(row)}`}
            title="แก้ไข"
            onClick={() => props.onEdit(row)}
          >
            <Pencil size={18} aria-hidden="true" />
          </ButtonV4>
        )}
        <ButtonV4
          variant="danger-ghost"
          size="icon"
          aria-label={`ยกเลิก ${codeOf(row)}`}
          title="ยกเลิก"
          disabled={!row.canCancel}
          onClick={() => props.onCancel(row)}
        >
          <Trash2 size={18} aria-hidden="true" />
        </ButtonV4>
      </>
    );
  };

  const renderRow = (row: DoneRow) => (
    <tr
      key={row.key}
      className={
        row.isCancelled
          ? "hv4-ord-done-cancelled"
          : row.isEditing
            ? "hv4-ord-done-editing"
            : undefined
      }
    >
      <td>
        <b className="hv4-ord-id">{codeOf(row)}</b>
        {!!createdTimeOf(row) && <div className="hv4-ord-id-at">สร้าง {createdTimeOf(row)}</div>}
        {row.isEditing && <div className="hv4-ord-done-editchip">กำลังแก้ไข</div>}
      </td>
      <td>
        <span className={`hv4-ord-done-pill${row.isCancelled ? " hv4-ord-done-pill--cancel" : ""}`}>
          {row.status}
        </span>
      </td>
      <td>
        <ul className="hv4-ord-done-items">
          {row.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </td>
      <td className="hv4-ord-done-num">
        <b>{formatComma(priceOf(row) ?? 0, "en-US", false)} ฿</b>
      </td>
      <td className="hv4-ord-done-act">
        <div className="hv4-ord-done-btns">
          {renderActions(row)}
          <ButtonV4
            variant="ghost"
            size="sm"
            aria-label={`Log ${codeOf(row)}`}
            title={
              row.order && isLogKind(row.category)
                ? "Log การสร้าง / แก้ไข Order"
                : "ยังไม่มี Log ของประเภทนี้"
            }
            disabled={!row.order || !isLogKind(row.category)}
            onClick={() => props.onLog(row)}
          >
            <History size={14} aria-hidden="true" /> Log
          </ButtonV4>
        </div>
      </td>
    </tr>
  );

  const renderGroup = (group: DoneGroup) => (
    <Fragment key={group.category}>
      <tr className="hv4-ord-done-group">
        <td colSpan={5}>
          <div className="hv4-ord-done-group-line">
            <ChipV4 size="sm" tone={CATEGORY_TONES[group.category]}>
              {CATEGORY_LABELS[group.category]}
            </ChipV4>
            <span className="hv4-ord-done-count">
              {group.rows.length} Order
              {group.cancelled > 0 ? ` (ยกเลิก ${group.cancelled})` : ""}
            </span>
            <b className="hv4-ord-done-sum">รวม {formatComma(group.total, "en-US", false)} ฿</b>
          </div>
        </td>
      </tr>
      {group.rows.map(renderRow)}
    </Fragment>
  );

  return (
    <section aria-label="คำสั่งที่ยืนยันแล้ว" className="hv4-ord-done">
      <h2 className="hv4-ord-done-title">คำสั่งที่ยืนยันแล้ว</h2>
      <div className="hv4-ord-done-wrap">
        <table className="hisv4-table hv4-ord-done-table">
          <thead>
            <tr>
              <th>รหัส Order</th>
              <th>Order status</th>
              <th>รายละเอียด</th>
              <th className="hv4-ord-done-num">ราคา</th>
              <th className="hv4-ord-done-act">จัดการ</th>
            </tr>
          </thead>
          <tbody>{props.groups.map(renderGroup)}</tbody>
        </table>
      </div>
    </section>
  );
};

OrderDoneTableV4.displayName = "OrderDoneTableV4";

export default OrderDoneTableV4;
